// « Le bon résultat »: a call situation, the rep picks one of the 8 outcomes. Options, labels and hotkeys come from
// DIAL_OUTCOMES and the dialer's own i18n; the numbers in the explanations (180 days, 30/45/60 min) come from the
// dialer constants. rightOutcome.test.ts runs each answer through validateOutcome / outcomeEffects.
import { NOT_INTERESTED_COOLDOWN_DAYS } from "../../domain/cooldown";
import { DEMO_DURATIONS, DIAL_OUTCOMES } from "../../domain/dialer/outcomes";
import type { DialOutcome } from "../../domain/dialer/types";
import type { QuizOption, QuizQuestion } from "./types";

const K = "gavion.training.content.right_outcome";

export const OUTCOME_OPTIONS: readonly QuizOption[] = DIAL_OUTCOMES.map(
  (o) => ({
    id: o.code,
    labelKey: `gavion.dialer.outcomes.${o.code}`,
    hotkey: o.hotkey,
  }),
);

const hotkeyOf = (code: DialOutcome) =>
  DIAL_OUTCOMES.find((o) => o.code === code)!.hotkey;

const round = (
  id: string,
  answer: DialOutcome,
  explanationArgs: QuizQuestion["explanationArgs"] = {},
): QuizQuestion => ({
  id,
  promptKey: `${K}.${id}.prompt`,
  options: OUTCOME_OPTIONS,
  answer,
  explanationKey: `${K}.${id}.explanation`,
  explanationArgs: { key: hotkeyOf(answer), ...explanationArgs },
});

/** Situation → outcome. Every one of the 8 outcomes is the answer once. */
export const OUTCOME_ROUNDS: readonly QuizQuestion[] = [
  round("rings_out", "no_answer"),
  round("voicemail", "voicemail"),
  round("callback_thursday", "talked_callback"),
  round("demo_tuesday", "demo_booked", {
    durations: DEMO_DURATIONS.join(", "),
  }),
  round("send_brochure", "brochure_sent"),
  round("simple_no", "not_interested", { days: NOT_INTERESTED_COOLDOWN_DAYS }),
  round("wrong_company", "wrong_number"),
  round("never_again", "do_not_call", { days: NOT_INTERESTED_COOLDOWN_DAYS }),
];

/** CASL: the « A accepté de recevoir nos courriels » box records an express consent; tick it only on a clear yes. */
export const CONSENT_ROUND: QuizQuestion = {
  id: "consent",
  promptKey: `${K}.consent.prompt`,
  promptArgs: { box: { label: "gavion.dialer.form.email_consent" } },
  options: [
    { id: "tick", labelKey: `${K}.consent.tick` },
    { id: "leave", labelKey: `${K}.consent.leave` },
  ],
  answer: "leave",
  explanationKey: `${K}.consent.explanation`,
  explanationArgs: { box: { label: "gavion.dialer.form.email_consent" } },
};

export const RIGHT_OUTCOME_QUESTIONS: readonly QuizQuestion[] = [
  ...OUTCOME_ROUNDS,
  CONSENT_ROUND,
];
